/**
 * @fileoverview Link Diagram Tool
 * 
 * Tool for linking shapes to other diagrams, enabling drill-down navigation.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import React, { useState, useMemo, useEffect } from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { LinkDiagramCommand } from '../../core/commands/LinkDiagramCommand';
import { UnlinkDiagramCommand } from '../../core/commands/UnlinkDiagramCommand';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useDashboard } from '../../hooks/useDashboard';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { Link2, Plus, Search, Unlink } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';
import { getDiagramNameForDisplay } from '@/dashboard/utils/diagramHelpers';


const SELECT_LABEL_MAX_CHARS = 34;

const truncateLabel = (text: string, maxChars = SELECT_LABEL_MAX_CHARS): string => {
  if (!text) return '';
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(0, maxChars - 3))}...`;
};

const LinkDiagramToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const { t: tDashboard } = useTranslation('dashboard');
  const [selectedDiagramId, setSelectedDiagramId] = useState<string>('');
  const [isLinking, setIsLinking] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('all');
  const [folderSearchQuery, setFolderSearchQuery] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [createName, setCreateName] = useState<string>(() =>
    t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string
  );
  const authenticatedFetch = useAuthenticatedFetch();
  const { diagramType: docDiagramType } = useDiagramDocument();

  // Get current diagram ID
  const currentDiagramId = useMemo(() => {
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string };
    return globalObj.__DIAGRAM_ID__ ?? '';
  }, []);

  const currentFolderIdFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/\/f\/([^/]+)\/d\/([^/]+)/);
    return m ? m[1] : null;
  }, []);

  // Get selected shape
  const selectedShapeId = useMemo(() => {
    if (context.selection.size !== 1) return null;
    return Array.from(context.selection)[0];
  }, [context.selection]);

  useEffect(() => {
    if (!selectedShapeId) return;
    setSelectedDiagramId('');
    setSelectedFolderId('all');
    setFolderSearchQuery('');
    setSearchQuery('');
    setCreateName(t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string);
  }, [selectedShapeId, t]);

  // Get shape data to check if it already has a link
  // Try multiple ways to get the shape data
  const shapeData = useMemo(() => {
    if (!selectedShapeId) return null;
    
    // Try from context.shapes first (if it's a Map)
    if (context.shapes && typeof context.shapes.get === 'function') {
      const shapeFromContext = context.shapes.get(selectedShapeId);
      if (shapeFromContext) {
        return shapeFromContext;
      }
    }
    
    return null;
  }, [selectedShapeId, context.shapes]);

  // Listen for unlink events to force re-render
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    const handleUnlink = (e: CustomEvent) => {
      if (e.detail?.shapeId === selectedShapeId) {
        console.log('[LinkDiagramTool] Received unlink event, forcing refresh');
        setRefreshKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('diagram:link:unlinked', handleUnlink as EventListener);
    return () => {
      window.removeEventListener('diagram:link:unlinked', handleUnlink as EventListener);
    };
  }, [selectedShapeId]);

  const existingLink = useMemo(() => {
    if (!shapeData) return null;
    const attrs = (shapeData as any).attrs || {};
    if (attrs.linkedDiagramId && attrs.linkId) {
      // Normalize linkId to ensure it's a clean string
      const linkId = String(attrs.linkId).trim();
      if (!linkId) return null;
      
      return {
        diagramId: String(attrs.linkedDiagramId),
        diagramName: String(
          attrs.linkedDiagramName ||
            (t('linksPanel.link.defaultLinkedDiagramName', { defaultValue: 'Linked Diagram' }) as string)
        ),
        linkId: linkId,
      };
    }
    return null;
  }, [shapeData, refreshKey, t]); // Add refreshKey to dependencies

  // Fetch diagrams for selection
  const { overview } = useDashboard();

  // Helper function to collect all diagrams from folder tree
  const collectAllDiagrams = (nodes: any[]): any[] => {
    const diagrams: any[] = [];
    const traverse = (node: any) => {
      if (node.diagrams) {
        diagrams.push(...node.diagrams);
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    };
    nodes.forEach(traverse);
    return diagrams;
  };

  // Helper to build folderId -> name mapping from dashboard folder trees
  const buildFolderNameMap = (roots: any[] | undefined): Map<string, string> => {
    const map = new Map<string, string>();
    const stack: any[] = Array.isArray(roots) ? [...roots] : [];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (typeof node.id === 'string' && typeof node.name === 'string') {
        map.set(node.id, node.name);
      }
      if (Array.isArray(node.children)) {
        for (const c of node.children) stack.push(c);
      }
    }
    return map;
  };

  // Extract all diagrams from dashboard overview
  const allDiagrams = useMemo(() => {
    if (!overview.data) {
      console.log('[LinkDiagramTool] No overview data available yet');
      return [];
    }
    
    const diagrams: any[] = [];
    
    // Add recent diagrams
    if (overview.data.recent) {
      diagrams.push(...overview.data.recent);
      console.log('[LinkDiagramTool] Added recent diagrams:', overview.data.recent.length);
    }
    
    // Add diagrams from personal roots
    if (overview.data.personal?.roots) {
      const personalDiagrams = collectAllDiagrams(overview.data.personal.roots);
      diagrams.push(...personalDiagrams);
      console.log('[LinkDiagramTool] Added personal diagrams:', personalDiagrams.length);
    }
    
    // Add diagrams from org roots
    if (overview.data.org?.roots) {
      const orgDiagrams = collectAllDiagrams(overview.data.org.roots);
      diagrams.push(...orgDiagrams);
      console.log('[LinkDiagramTool] Added org diagrams:', orgDiagrams.length);
    }
    
    // Add diagrams from shared with me
    if (overview.data.sharedWithMe?.roots) {
      const sharedDiagrams = collectAllDiagrams(overview.data.sharedWithMe.roots);
      diagrams.push(...sharedDiagrams);
      console.log('[LinkDiagramTool] Added shared diagrams:', sharedDiagrams.length);
    }
    
    // Add diagrams from external orgs
    if (overview.data.externalOrgs) {
      overview.data.externalOrgs.forEach(org => {
        if (org.roots) {
          const extDiagrams = collectAllDiagrams(org.roots);
          diagrams.push(...extDiagrams);
          console.log('[LinkDiagramTool] Added external org diagrams:', extDiagrams.length);
        }
      });
    }
    
    // Remove duplicates by ID (keep first occurrence)
    const uniqueDiagrams = new Map();
    diagrams.forEach(d => {
      if (!uniqueDiagrams.has(d.id)) {
        uniqueDiagrams.set(d.id, d);
      }
    });
    
    const unique = Array.from(uniqueDiagrams.values());
    console.log('[LinkDiagramTool] Total unique diagrams:', unique.length, 'Current diagram ID:', currentDiagramId);
    return unique;
  }, [overview.data, currentDiagramId]);

  // Filter out current diagram from list
  const availableDiagrams = useMemo(() => {
    return allDiagrams.filter(d => d.id !== currentDiagramId);
  }, [allDiagrams, currentDiagramId]);

  const folderNameById = useMemo(() => {
    if (!overview.data) return new Map<string, string>();
    const maps = [
      buildFolderNameMap(overview.data.personal?.roots),
      buildFolderNameMap(overview.data.org?.roots),
      buildFolderNameMap(overview.data.sharedWithMe?.roots),
      buildFolderNameMap(overview.data.archived?.roots),
    ];
    if (Array.isArray(overview.data.externalOrgs)) {
      overview.data.externalOrgs.forEach((org: any) => {
        maps.push(buildFolderNameMap(org?.roots));
      });
    }
    // Merge (later maps don't matter; names should be identical for same id)
    const merged = new Map<string, string>();
    for (const m of maps) {
      m.forEach((v, k) => merged.set(k, v));
    }
    return merged;
  }, [overview.data]);

  const folderOptions = useMemo(() => {
    const ids = new Set<string>();
    availableDiagrams.forEach((d: any) => {
      if (typeof d?.folderId === 'string' && d.folderId) ids.add(d.folderId);
    });
    if (currentFolderIdFromUrl) ids.add(currentFolderIdFromUrl);

    const getFolderLabel = (id: string) => folderNameById.get(id) || `Folder ${id.slice(0, 8)}...`;

    const currentId = currentFolderIdFromUrl;
    const currentOpt = currentId
      ? {
          id: currentId,
          name: `${getFolderLabel(currentId)} ${t('linksPanel.link.currentFolderSuffix', { defaultValue: '(current)' })}`,
        }
      : null;

    const opts = Array.from(ids)
      .filter((id) => id !== currentId)
      .map((id) => ({ id, name: getFolderLabel(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [
      { id: 'all', name: t('linksPanel.link.allFolders', { defaultValue: 'All folders' }) as string },
      ...(currentOpt ? [currentOpt] : []),
      ...opts,
    ];
  }, [availableDiagrams, folderNameById, currentFolderIdFromUrl, t]);

  const selectedFolderName = useMemo(() => {
    if (selectedFolderId === 'all') return null;
    return folderNameById.get(selectedFolderId) || `Folder ${selectedFolderId.slice(0, 8)}...`;
  }, [selectedFolderId, folderNameById]);

  const selectedFolderFullLabel = useMemo(() => {
    const selected = folderOptions.find((folder) => folder.id === selectedFolderId);
    return selected?.name || (t('linksPanel.link.allFolders', { defaultValue: 'All folders' }) as string);
  }, [folderOptions, selectedFolderId, t]);

  const selectedDiagramFullLabel = useMemo(() => {
    if (!selectedDiagramId) return '';
    const selected = availableDiagrams.find((diagram: any) => diagram.id === selectedDiagramId);
    const untitled = t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string;
    const raw = (selected?.name as string | undefined) ?? '';
    if (!String(raw).trim()) return untitled;
    return getDiagramNameForDisplay(String(raw).trim(), t, tDashboard);
  }, [availableDiagrams, selectedDiagramId, t, tDashboard]);

  const showSelectedFolderTooltip = selectedFolderFullLabel.length > SELECT_LABEL_MAX_CHARS;
  const showSelectedDiagramTooltip = selectedDiagramFullLabel.length > SELECT_LABEL_MAX_CHARS;

  const filteredFolderOptions = useMemo(() => {
    const q = folderSearchQuery.trim().toLowerCase();
    if (!q) return folderOptions;
    const matches = folderOptions.filter((folder) => {
      const name = folder.name.toLowerCase();
      return name.startsWith(q) || name.includes(q);
    });

    // Keep the active selection in the list to avoid Select focus instability while typing.
    if (selectedFolderId && !matches.some((folder) => folder.id === selectedFolderId)) {
      const selected = folderOptions.find((folder) => folder.id === selectedFolderId);
      if (selected) matches.unshift(selected);
    }

    return matches.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return aName.localeCompare(bName);
    });
  }, [folderOptions, folderSearchQuery, selectedFolderId]);

  const filteredDiagrams = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const untitled = t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string;
    const matches = availableDiagrams.filter((d: any) => {
      if (selectedFolderId !== 'all' && d.folderId !== selectedFolderId) return false;
      if (q) {
        const raw = String(d.name || '').trim();
        const name = raw.toLowerCase();
        const display = (raw
          ? getDiagramNameForDisplay(raw, t, tDashboard)
          : untitled
        ).toLowerCase();
        if (
          !name.startsWith(q) &&
          !name.includes(q) &&
          !display.startsWith(q) &&
          !display.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });

    // Keep the active selection in the list to avoid Select focus instability while typing.
    if (selectedDiagramId && !matches.some((d: any) => d.id === selectedDiagramId)) {
      const selected = availableDiagrams.find((d: any) => d.id === selectedDiagramId);
      if (selected) matches.unshift(selected);
    }

    if (!q) return matches;

    return matches.sort((a: any, b: any) => {
      const aName = String(a.name || '').toLowerCase();
      const bName = String(b.name || '').toLowerCase();
      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return aName.localeCompare(bName);
    });
  }, [availableDiagrams, searchQuery, selectedFolderId, selectedDiagramId, t, tDashboard]);

  // Keep selection valid when filters change
  useEffect(() => {
    if (!selectedDiagramId) return;
    const exists = filteredDiagrams.some((d: any) => d.id === selectedDiagramId);
    if (!exists) setSelectedDiagramId('');
  }, [filteredDiagrams, selectedDiagramId]);

  const isLoading = overview.isLoading;

  const handleLink = async () => {
    if (!selectedDiagramId || !selectedShapeId || !currentDiagramId) {
      console.warn('LinkDiagramTool: Missing required data', { selectedDiagramId, selectedShapeId, currentDiagramId });
      return;
    }

    const targetDiagram = filteredDiagrams.find(d => d.id === selectedDiagramId) || availableDiagrams.find(d => d.id === selectedDiagramId);
    if (!targetDiagram) {
      console.warn('LinkDiagramTool: Target diagram not found', selectedDiagramId);
      return;
    }

    setIsLinking(true);
    try {
      // Call onExecute with params - the tool registry will handle creating the command
      onExecute({
        action: 'link',
        targetDiagramId: selectedDiagramId,
        targetDiagramName: (() => {
          const raw = String((targetDiagram as { name?: string }).name ?? '').trim();
          if (!raw) {
            return t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string;
          }
          return getDiagramNameForDisplay(raw, t, tDashboard);
        })(),
      });
    } catch (error) {
      console.error('Failed to create link:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleCreateAndLink = async () => {
    if (!selectedShapeId || !currentDiagramId) {
      console.warn('LinkDiagramTool: Missing required data for create+link', { selectedShapeId, currentDiagramId });
      return;
    }

    // Creating a diagram requires an explicit folder selection in the UI.
    if (selectedFolderId === 'all') return;
    const resolvedFolderId = selectedFolderId;

    const name =
      (createName || '').trim() ||
      (t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string);
    // Use the current document's diagram type so the new diagram opens in the same domain.
    const type = (docDiagramType as any) || (context.diagramType as any) || 'genogram';

    setIsLinking(true);
    try {
      const queryParams = new URLSearchParams({ name, type });
      if (resolvedFolderId) queryParams.set('folderId', resolvedFolderId);
      const resp = await authenticatedFetch(`/api/diagrams/new?${queryParams.toString()}`);
      if (!resp.ok) {
        console.error('LinkDiagramTool: Failed to create diagram', resp.status);
        return;
      }
      const data = await resp.json();
      const newDiagramId = String(data?.diagramId || '').trim();
      if (!newDiagramId) {
        console.error('LinkDiagramTool: Missing diagramId from create response');
        return;
      }

      onExecute({
        action: 'link',
        targetDiagramId: newDiagramId,
        targetDiagramName: name,
      });
    } catch (error) {
      console.error('LinkDiagramTool: Failed to create+link diagram', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!existingLink || !selectedShapeId || !currentDiagramId) {
      console.warn('LinkDiagramTool: Missing required data for unlink', { existingLink, selectedShapeId, currentDiagramId });
      return;
    }

    // Normalize linkId (trim whitespace, ensure it's a string)
    const normalizedLinkId = String(existingLink.linkId || '').trim();
    if (!normalizedLinkId) {
      console.error('LinkDiagramTool: Invalid linkId', { existingLink, linkId: existingLink.linkId });
      return;
    }

    console.log('[LinkDiagramTool] Unlinking diagram:', { 
      linkId: normalizedLinkId, 
      originalLinkId: existingLink.linkId,
      linkIdLength: normalizedLinkId.length,
      selectedShapeId,
      currentDiagramId 
    });

    setIsLinking(true);
    try {
      console.log('[LinkDiagramTool] Calling onExecute with unlink params');
      // Call onExecute with params - the tool registry will handle creating the command
      onExecute({
        action: 'unlink',
        linkId: normalizedLinkId,
      });
      console.log('[LinkDiagramTool] onExecute called');
    } catch (error) {
      console.error('[LinkDiagramTool] Failed to remove link:', error);
    } finally {
      setIsLinking(false);
    }
  };

  if (context.selection.size !== 1) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground">
        {t('linksPanel.selectionHint', { defaultValue: 'Select a single shape to link to another diagram.' })}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="px-4 py-3 space-y-4">
        <div className="space-y-2">
          <Label className="text-xs font-medium">
            {t('linksPanel.link.sectionTitle', { defaultValue: 'Link to Diagram' })}
          </Label>

        {existingLink ? (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              {t('linksPanel.link.linkedToLabel', { defaultValue: 'Linked to:' })}{' '}
              <span className="font-medium">{existingLink.diagramName}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="tid.tools.linkDiagram.unlink"
              onClick={handleUnlink}
              disabled={isLinking}
              className="w-full"
            >
              <Unlink className="h-4 w-4 mr-2" />
              {t('linksPanel.link.unlinkButton', { defaultValue: 'Unlink Diagram' })}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                {t('linksPanel.link.folderLabel', { defaultValue: 'Folder' })}
              </Label>
              <Select
                value={selectedFolderId}
                onValueChange={setSelectedFolderId}
                disabled={isLoading || isLinking}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SelectTrigger className="w-full overflow-hidden">
                      <SelectValue
                        className="block min-w-0 max-w-[calc(100%-1.25rem)] truncate"
                        placeholder={t('linksPanel.link.allFolders', { defaultValue: 'All folders' }) as string}
                      >
                        {selectedFolderId ? truncateLabel(selectedFolderFullLabel) : null}
                      </SelectValue>
                    </SelectTrigger>
                  </TooltipTrigger>
                  {showSelectedFolderTooltip ? (
                    <TooltipContent className="!w-[280px] [text-wrap:wrap!important] whitespace-normal break-words text-left">{selectedFolderFullLabel}</TooltipContent>
                  ) : null}
                </Tooltip>
                <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] [&>[data-slot=select-scroll-up-button]]:hidden [&_[data-radix-select-viewport]]:p-0">
                  <div className="sticky top-0 z-20 bg-popover px-2 py-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      <Input
                        value={folderSearchQuery}
                        onChange={(e) => setFolderSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder={t('linksPanel.link.searchFoldersPlaceholder', { defaultValue: 'Search folders...' })}
                        disabled={isLoading || isLinking}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  {filteredFolderOptions.length === 0 ? (
                    <SelectItem value="no-folders" disabled className="pl-3 pr-9">
                      {t('linksPanel.link.noMatchingFolders', { defaultValue: 'No matching folders' })}
                    </SelectItem>
                  ) : filteredFolderOptions.map((f) => {
                    const showNameTooltip = f.name.length > SELECT_LABEL_MAX_CHARS;
                    return (
                      <SelectItem
                        key={f.id}
                        value={f.id}
                        className="w-full pl-3 pr-9 cursor-pointer [&>span:last-child]:block [&>span:last-child]:truncate"
                      >
                        {showNameTooltip ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block w-full truncate">{f.name}</span>
                            </TooltipTrigger>
                            <TooltipContent className="!w-[280px] [text-wrap:wrap!important] whitespace-normal break-words text-left">{f.name}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="block w-full truncate">{f.name}</span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">
                {t('linksPanel.link.diagramLabel', { defaultValue: 'Diagram' })}
              </Label>
              <Select
                value={selectedDiagramId}
                onValueChange={setSelectedDiagramId}
                disabled={isLoading || isLinking}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SelectTrigger className="w-full overflow-hidden">
                      <SelectValue
                        className="block min-w-0 max-w-[calc(100%-1.25rem)] truncate"
                        placeholder={t('linksPanel.link.selectDiagramPlaceholder', { defaultValue: 'Select a diagram...' }) as string}
                      >
                        {selectedDiagramId ? truncateLabel(selectedDiagramFullLabel) : null}
                      </SelectValue>
                    </SelectTrigger>
                  </TooltipTrigger>
                  {showSelectedDiagramTooltip ? (
                    <TooltipContent className="!w-[280px] [text-wrap:wrap!important] whitespace-normal break-words text-left">{selectedDiagramFullLabel}</TooltipContent>
                  ) : null}
                </Tooltip>
                <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] [&>[data-slot=select-scroll-up-button]]:hidden [&_[data-radix-select-viewport]]:p-0">
                  <div className="sticky top-0 z-20 bg-popover px-2 py-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder={t('linksPanel.link.searchPlaceholder', { defaultValue: 'Search diagrams...' }) as string}
                        disabled={isLoading || isLinking}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  {isLoading ? (
                    <SelectItem value="loading" disabled className="pl-3 pr-9">
                      {t('linksPanel.link.loadingDiagrams', { defaultValue: 'Loading diagrams...' })}
                    </SelectItem>
                  ) : filteredDiagrams.length === 0 ? (
                    <SelectItem value="no-diagrams" disabled className="pl-3 pr-9">
                      {t('linksPanel.link.noMatchingDiagrams', { defaultValue: 'No matching diagrams' })}
                    </SelectItem>
                  ) : (
                    filteredDiagrams.map((diagram) => {
                      const raw = String((diagram as { name?: string }).name ?? '').trim();
                      const diagramName = raw
                        ? getDiagramNameForDisplay(raw, t, tDashboard)
                        : (t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string);
                      const showNameTooltip = diagramName.length > SELECT_LABEL_MAX_CHARS;
                      return (
                        <SelectItem
                          key={diagram.id}
                          value={diagram.id}
                          className="w-full pl-3 pr-9 cursor-pointer [&>span:last-child]:block [&>span:last-child]:truncate"
                        >
                          {showNameTooltip ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block w-full truncate">{diagramName}</span>
                              </TooltipTrigger>
                              <TooltipContent className="!w-[280px] [text-wrap:wrap!important] whitespace-normal break-words text-left">{diagramName}</TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="block w-full truncate">{diagramName}</span>
                          )}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="default"
              size="sm"
              data-testid="tid.tools.linkDiagram.link"
              onClick={handleLink}
              disabled={!selectedDiagramId || isLoading || isLinking}
              className="w-full"
            >
              <Link2 className="h-4 w-4 mr-2" />
              {t('linksPanel.link.linkButton', { defaultValue: 'Link Diagram' })}
            </Button>

            <div className="pt-2 mt-2 border-t">
              <div className="text-xs text-muted-foreground mb-2">
                {selectedFolderId === 'all'
                  ? t('linksPanel.link.orCreateNewDiagram', { defaultValue: 'Or create a new diagram' })
                  : t('linksPanel.link.orCreateNewDiagramInFolder', {
                      folder:
                        selectedFolderName ||
                        (t('linksPanel.link.selectedFolderFallback', { defaultValue: 'selected folder' }) as string),
                      defaultValue: 'Or create a new diagram in {{folder}}',
                    })}
              </div>
              <div className="space-y-2">
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder={t('linksPanel.link.newDiagramNamePlaceholder', { defaultValue: 'New diagram name...' }) as string}
                  disabled={isLinking}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="tid.tools.linkDiagram.createAndLink"
                          onClick={handleCreateAndLink}
                          disabled={isLinking || selectedFolderId === 'all'}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t('linksPanel.link.createAndLinkButton', { defaultValue: 'Create & Link' })}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {selectedFolderId === 'all' ? (
                      <TooltipContent>
                        {t('linksPanel.link.selectFolderTooltip', { defaultValue: 'Select a folder' })}
                      </TooltipContent>
                    ) : null}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export class LinkDiagramTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'link-diagram',
    name: i18n.t('diagram:linksPanel.tool.name', { defaultValue: 'Link Diagram' }) as string,
    icon: 'link',
    category: 'edit',
    description: i18n.t('diagram:linksPanel.tool.description', {
      defaultValue: 'Link shape to another diagram for drill-down navigation',
    }) as string,
    version: '1.0.0',
    
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1, // Only single selection
    
    // Support all diagram types
    supportedDiagramTypes: ['generic', 'flowchart', 'genogram', 'org', 'mindmap', 'network', 'database', 'uml'],
    
    ui: {
      toolbar: false,
      contextMenu: false,
      // Rendered inside the Links side panel (not in the Properties panel tool list).
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size === 1;
  }

  execute(params?: { action?: string; targetDiagramId?: string; targetDiagramName?: string; linkId?: string }): ICommand | null {
    if (!this.context || this.context.selection.size !== 1) {
      return null;
    }

    const selectedShapeId = Array.from(this.context.selection)[0];
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string };
    const currentDiagramId = globalObj.__DIAGRAM_ID__ ?? '';

    if (!currentDiagramId) {
      return null;
    }

    if (params?.action === 'link' && params.targetDiagramId && params.targetDiagramName) {
      return new LinkDiagramCommand({
        sourceDiagramId: currentDiagramId,
        sourceShapeId: selectedShapeId,
        targetDiagramId: params.targetDiagramId,
        targetDiagramName: params.targetDiagramName,
        linkType: 'subprocess',
      });
    }

    if (params?.action === 'unlink' && params.linkId) {
      return new UnlinkDiagramCommand({
        sourceDiagramId: currentDiagramId,
        sourceShapeId: selectedShapeId,
        linkId: params.linkId,
      });
    }

    return null;
  }

  getPropertyPanelComponent() {
    return LinkDiagramToolComponent;
  }
}
